import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";
import type { DimensionPoste } from "../../types/dimension";
import type { ModuleAnalyticsResponse } from "./useGetModuleAnalytics";
import { parseAnalyticsData } from "./useGetModuleAnalytics";

export interface RawEnergieGroupEntry {
  groupId: number;
  groupName: string;
  total: number;
  timeSeries: { month: string; valeur: number }[];
}

export interface RawEnergieTypeResult {
  valeurId: number;
  valeurCode: string;
  valeurLabel: string;
  groups: RawEnergieGroupEntry[];
  allMonths: string[];
}

interface UseGetRawEnergiePerTypeBUOptions {
  baseFilters: {
    from_period: string;
    to_period: string;
    site_ids?: number[];
    bu_ids?: number[];
    activite_ids?: number[];
    filiere_ids?: number[];
    region_ids?: number[];
  };
  champMetierId: number | null;
  energieDimension: DimensionPoste | null;
  perBUIds: number[];
  buNamesMap: Record<number, string>;
  enabled?: boolean;
}

/**
 * Hook that fetches raw energy saisie data (in native units) per dimension value per BU.
 * Uses the /core/saisie-analytics/ endpoint with `selections` to filter by energie_type.
 * Returns data organized by valeur code (ELEC, GASOIL, ESSENCE, FUEL, BUTANE, etc.)
 */
export const useGetRawEnergiePerTypeBU = ({
  baseFilters,
  champMetierId,
  energieDimension,
  perBUIds,
  buNamesMap,
  enabled = true,
}: UseGetRawEnergiePerTypeBUOptions) => {
  // Build flat list of (valeur, buId) query combinations
  const combinations = useMemo(() => {
    if (!energieDimension?.valeurs?.length || !champMetierId || !perBUIds.length) return [];
    const combos: {
      valeurId: number;
      valeurCode: string;
      valeurLabel: string;
      buId: number;
    }[] = [];
    for (const v of energieDimension.valeurs) {
      for (const buId of perBUIds) {
        combos.push({
          valeurId: v.id,
          valeurCode: v.code || v.valeur.toUpperCase().replace(/\s+/g, "_"),
          valeurLabel: v.valeur,
          buId,
        });
      }
    }
    return combos;
  }, [energieDimension, champMetierId, perBUIds]);

  const results = useQueries({
    queries: combinations.map((combo) => ({
      queryKey: [
        "rawEnergiePerTypeBU",
        combo.valeurId,
        combo.buId,
        baseFilters,
        champMetierId,
      ],
      queryFn: async () => {
        const filters: Record<string, unknown> = {};
        Object.entries(baseFilters).forEach(([key, value]) => {
          if (value !== null && value !== undefined) filters[key] = value;
        });
        filters["bu_ids"] = [combo.buId];
        filters["champ_metier"] = champMetierId;
        if (energieDimension) {
          filters["selections"] = [
            { dimension_id: energieDimension.id, valeur_id: combo.valeurId },
          ];
        }

        const response = await fetchClient<ModuleAnalyticsResponse>(
          "/core/analytics/energie/",
          { method: "POST", body: filters }
        );

        const { total, timeSeries } = parseAnalyticsData(response.data);
        return {
          valeurId: combo.valeurId,
          valeurCode: combo.valeurCode,
          valeurLabel: combo.valeurLabel,
          buId: combo.buId,
          buName: buNamesMap[combo.buId] || `BU ${combo.buId}`,
          total,
          timeSeries,
        };
      },
      enabled:
        enabled &&
        !!champMetierId &&
        !!energieDimension &&
        perBUIds.length > 0,
      staleTime: 1000 * 60 * 5,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);

  // Organize results by valeur code → { groups per BU, allMonths }
  const dataByType = useMemo<Record<string, RawEnergieTypeResult>>(() => {
    const organized: Record<string, RawEnergieTypeResult> = {};

    for (const r of results) {
      if (!r.data) continue;
      const { valeurId, valeurCode, valeurLabel, buId, buName, total, timeSeries } =
        r.data;

      if (!organized[valeurCode]) {
        organized[valeurCode] = {
          valeurId,
          valeurCode,
          valeurLabel,
          groups: [],
          allMonths: [],
        };
      }

      if (total > 0) {
        organized[valeurCode].groups.push({
          groupId: buId,
          groupName: buName,
          total,
          timeSeries,
        });
      }
    }

    // Compute allMonths and sort groups by total desc for each type
    for (const type of Object.values(organized)) {
      type.allMonths = Array.from(
        new Set(type.groups.flatMap((g) => g.timeSeries.map((t) => t.month)))
      ).sort();
      type.groups.sort((a, b) => b.total - a.total);
    }

    return organized;
  }, [results]);

  return { dataByType, isLoading, combinationCount: combinations.length };
};
